import { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { api, type Account, ApiError } from './api';
import SignIn from './pages/SignIn';
import Overview from './pages/Overview';
import Keys from './pages/Keys';
import Generations from './pages/Generations';
import WebhookPage from './pages/Webhook';
import Docs from './pages/Docs';

type State =
  | { phase: 'loading' }
  | { phase: 'signed-out' }
  | { phase: 'no-access' }
  | { phase: 'ready'; account: Account; displayName?: string };

export default function App() {
  const [state, setState] = useState<State>({ phase: 'loading' });

  const load = useCallback(async () => {
    let displayName: string | undefined;
    try {
      const session = await api.session();
      if (!session.authenticated) {
        setState({ phase: 'signed-out' });
        return;
      }
      displayName = session.user?.email || session.user?.display_name;
    } catch {
      setState({ phase: 'signed-out' });
      return;
    }

    try {
      setState({ phase: 'ready', account: await api.account(), displayName });
    } catch (error) {
      // 404 is a signed-in user who has not enabled API access yet — a state
      // with a button, not an error.
      if (error instanceof ApiError && error.status === 404) {
        setState({ phase: 'no-access' });
        return;
      }
      setState({ phase: 'signed-out' });
    }
  }, []);

  useEffect(() => {
    // "Or open this link" in the sign-in email. The address and the code are
    // inside the token, and it leaves the address bar before anything else so
    // a shared screen or a copied URL does not carry a live credential.
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) {
      void load();
      return;
    }
    params.delete('token');
    const rest = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    void api
      .verifyEmailToken(token)
      .catch(() => undefined)
      .finally(() => void load());
  }, [load]);

  // Any call that comes back 401 means the session is gone; every page would
  // otherwise show its own "could not load" line inside a cabinet that still
  // looks signed in.
  useEffect(() => {
    const onExpired = () => setState({ phase: 'signed-out' });
    window.addEventListener('api-unauthorized', onExpired);
    return () => window.removeEventListener('api-unauthorized', onExpired);
  }, []);

  if (state.phase === 'loading') {
    return <div className="content muted">Loading…</div>;
  }

  if (state.phase === 'signed-out') {
    return <SignIn onSignedIn={load} />;
  }

  if (state.phase === 'no-access') {
    return <EnableAccess onEnabled={load} />;
  }

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">
          <img className="brand-mark" src="/favicon.png" alt="" />
          <span>
            <h1>Imaginai</h1>
            <span className="brand-sub">Developer API</span>
          </span>
        </div>
        <nav>
          <NavLink to="/" end>
            Overview
          </NavLink>
          <NavLink to="/generations">Generations</NavLink>
          <NavLink to="/keys">Keys</NavLink>
          <NavLink to="/webhook">Webhook</NavLink>
          <NavLink to="/docs">Docs</NavLink>
        </nav>
        <div className="who">
          {/* The email, not the generated account name:
              "web-ui-dev-999000222" is our identifier for the account and means
              nothing to the person reading it. */}
          <span className="who-name">{state.displayName || state.account.name}</span>
          <span className="who-sub">
            {state.account.status === 'ACTIVE' ? 'API access on' : 'Account suspended'}
          </span>
          <br />
          <button
            className="link"
            onClick={async () => {
              await api.logout().catch(() => undefined);
              setState({ phase: 'signed-out' });
            }}
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="content">
        <Routes>
          <Route path="/" element={<Overview account={state.account} onChanged={load} />} />
          <Route path="/keys" element={<Keys />} />
          <Route path="/generations" element={<Generations />} />
          <Route path="/webhook" element={<WebhookPage />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function EnableAccess({ onEnabled }: { onEnabled: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  return (
    <main className="signin">
      <div className="signin-card">
        <div className="brand">
          <img className="brand-mark" src="/favicon.png" alt="" />
          <span>
            <h1>Imaginai</h1>
            <span className="brand-sub">Developer API</span>
          </span>
        </div>
        <div className="panel">
        <h3>Turn on API access</h3>
        <p className="muted small">
          This creates your API account and a prepaid balance. Nothing is charged until you run a
          generation, and the balance is separate from any credits you hold in the bot.
        </p>
        {error ? <p className="error">{error}</p> : null}
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError('');
            try {
              await api.enable();
              onEnabled();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Could not enable API access');
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Enabling…' : 'Enable API access'}
        </button>
        </div>
      </div>
    </main>
  );
}
